/**
 * IdentityIQConnectCard — Phase F1 (Credit Upload Lockdown).
 *
 * The user-facing surface that replaces the manual credit-report upload flow
 * everywhere in the product. Renders a prominent "Connect IdentityIQ" card
 * whose primary action opens a modal explaining that the connection is
 * currently in provisioning and no user action is required.
 *
 * Copy is fixed by product direction — do NOT introduce banned words:
 *   "coming soon", "beta", "placeholder", "guaranteed", "we'll fix",
 *   "will remove", "lorem".
 *
 * The card is intentionally provider-exclusive to IdentityIQ. No competitor
 * logos, names, or referral links appear anywhere in this component or its
 * modal.
 *
 * Data model preservation: the admin-editable
 * `credit_hero_referral_url` / `credit_hero_enabled` fields on
 * `SiteSettings` are **not** touched here. They remain in the backend for
 * potential future re-use; this component simply does not consume them.
 */
import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, Info, PlugZap, ShieldCheck, X } from "lucide-react";
import { useFocusTrap } from "@/design-system/hooks/useFocusTrap";

const CARD_VARIANTS = {
  // Full-bleed card used inside the credit-report page hero.
  page: {
    padding: "px-6 py-12 md:px-10 md:py-16",
    iconSize: "h-11 w-11",
    titleClass: "text-v2-heading-lg",
    subtitleWidth: "max-w-md",
  },
  // Compact card used inside dashboard grids.
  dashboard: {
    padding: "px-6 py-12 md:px-10 md:py-16",
    iconSize: "h-11 w-11",
    titleClass: "text-v2-heading-lg",
    subtitleWidth: "max-w-md",
  },
};

export default function IdentityIQConnectCard({
  variant = "dashboard",
  disclaimer,
  consent,
  testIdPrefix = "identityiq-connect",
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [consentOk, setConsentOk] = useState(false);
  // Phase 6.2 — hold a ref to the CTA button so useFocusTrap can restore
  // focus reliably on modal close, even when the button was activated
  // programmatically (dispatchEvent, test harnesses) or the user is on
  // Safari (which doesn't focus buttons on mouse click).
  const triggerRef = useRef(null);
  const cfg = CARD_VARIANTS[variant] || CARD_VARIANTS.dashboard;

  // The consent checkbox is only shown when the caller supplies consent copy
  // (i.e., the credit-report page variant). Dashboard grid usage renders the
  // card without a per-instance consent — the disclaimer lives once at the
  // page level.
  const requiresConsent = Boolean(consent);
  const canOpen = requiresConsent ? consentOk : true;

  return (
    <>
      <div
        data-testid={`${testIdPrefix}-card`}
        className={`rounded-v2-2xl bg-v2-surface border border-v2-border shadow-v2-subtle ${cfg.padding} flex flex-col items-center text-center gap-5`}
      >
        <span className="inline-flex h-11 w-11 rounded-v2-md bg-v2-accentSoft text-v2-accentStrong items-center justify-center">
          <PlugZap className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="text-v2-caption uppercase text-v2-accentStrong">
          Connect Your IdentityIQ Account
        </div>
        <h3
          data-testid={`${testIdPrefix}-title`}
          className={`${cfg.titleClass} text-v2-text`}
          style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
        >
          One connection. All three bureaus.
        </h3>
        <p
          data-testid={`${testIdPrefix}-subtitle`}
          className={`text-v2-body-md text-v2-textMuted ${cfg.subtitleWidth}`}
        >
          IdentityIQ pulls your TransUnion, Equifax, and Experian data in one
          place so we can analyze the full picture — bureau by bureau —
          without asking you to hunt down PDFs.
        </p>

        {disclaimer ? (
          <div
            data-testid={`${testIdPrefix}-disclaimer`}
            className="rounded-v2-md bg-v2-accentSoft/40 border border-v2-accent/20 px-4 py-3 text-v2-body-sm text-v2-textMuted text-left"
          >
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-v2-accentStrong" aria-hidden="true" />
              <p className="leading-relaxed">{disclaimer}</p>
            </div>
          </div>
        ) : null}

        {requiresConsent ? (
          <label
            data-testid={`${testIdPrefix}-consent-checkbox`}
            className="flex items-start gap-3 cursor-pointer text-left max-w-xl"
          >
            <input
              type="checkbox"
              checked={consentOk}
              onChange={(e) => setConsentOk(e.target.checked)}
              className="mt-1 accent-v2-accent"
            />
            <span className="text-v2-body-sm text-v2-textMuted leading-relaxed">
              {consent}
            </span>
          </label>
        ) : null}

        <button
          ref={triggerRef}
          type="button"
          data-testid={`${testIdPrefix}-cta`}
          disabled={!canOpen}
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center justify-center gap-2 rounded-v2-md bg-v2-accent px-5 py-2.5 text-v2-body-md font-semibold text-v2-accentInk hover:bg-v2-accentStrong shadow-v2-card hover:shadow-v2-lifted transition-all disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <ShieldCheck className="h-4 w-4" aria-hidden="true" />
          Connect IdentityIQ
        </button>

        <p
          data-testid={`${testIdPrefix}-footnote`}
          className="text-v2-caption text-v2-textSubtle max-w-xl"
        >
          Your existing analysis stays available — connecting IdentityIQ adds
          fresh bureau data on top of what you&rsquo;ve already reviewed.
        </p>
      </div>

      {modalOpen ? (
        <IdentityIQProvisioningModal
          testIdPrefix={testIdPrefix}
          onClose={() => setModalOpen(false)}
          returnFocusRef={triggerRef}
        />
      ) : null}
    </>
  );
}

function IdentityIQProvisioningModal({ onClose, testIdPrefix, returnFocusRef }) {
  const panelRef = useRef(null);
  const labelId = React.useId();
  const descId = React.useId();

  useFocusTrap(panelRef, true, { onEscape: onClose, returnFocusRef });

  return (
    <div
      data-testid={`${testIdPrefix}-modal-backdrop`}
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(0,0,0,0.65)" }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        data-testid={`${testIdPrefix}-modal`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        aria-describedby={descId}
        tabIndex={-1}
        className="w-full max-w-lg rounded-v2-2xl bg-v2-surface border border-v2-border shadow-v2-lifted p-6 md:p-8 outline-none"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-v2-caption uppercase text-v2-accentStrong">
              IdentityIQ · Connection in provisioning
            </div>
            <h3
              id={labelId}
              className="mt-1 text-v2-heading-md text-v2-text"
              style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
            >
              Your workspace is being authorized
            </h3>
          </div>
          <button
            type="button"
            data-testid={`${testIdPrefix}-modal-close`}
            onClick={onClose}
            className="text-v2-textMuted hover:text-v2-text"
            aria-label="Close"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <p
          id={descId}
          data-testid={`${testIdPrefix}-modal-body`}
          className="text-v2-body-md text-v2-textMuted leading-relaxed"
        >
          IdentityIQ account connection is undergoing provisioning. Once your
          workspace is authorized, this connection will pull all three
          bureaus automatically. In the meantime, no action is required —
          check back shortly.
        </p>

        <div
          data-testid={`${testIdPrefix}-modal-status`}
          className="mt-6 rounded-v2-md bg-v2-successSoft border border-v2-success/30 px-4 py-3 text-v2-body-sm text-v2-success flex items-start gap-2"
        >
          <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <span>
            Your existing credit analysis remains available on the Credit
            Readiness and Credit Improvement pages while provisioning
            completes.
          </span>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            data-testid={`${testIdPrefix}-modal-dismiss`}
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-v2-md bg-v2-accent px-4 py-2 text-v2-body-sm font-semibold text-v2-accentInk hover:bg-v2-accentStrong shadow-v2-card hover:shadow-v2-lifted transition-all"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

export { IdentityIQProvisioningModal };
